import type { Recipe } from '../types';

// Soft sandwich rolls, stand-mixer version. Doubled from the source so one
// batch covers a meatball-sub night and leaves rolls in the freezer.

export const hoagieRolls: Recipe = {
  slug: 'hoagie-rolls',
  title: 'Hoagie Rolls',
  meal: 'lunch',
  category: 'baked',
  types: ['bread'],
  cuisines: ['american'],
  traits: ['freezer-friendly', 'serve-with', 'stand-mixer'],
  yield: '8 six-inch rolls',
  activeTime: '25 min',
  totalTime: '2 hr 15 min',
  freezerLife: '3 months',
  difficulty: 2,

  summary:
    'One bowl in the KitchenAid, two rises, fifteen minutes in the oven. Soft enough to bite through a loaded meatball sub, sturdy enough not to fall apart under the sauce. Most of the two hours is waiting on dough.',

  flavorNotes:
    'Soft and slightly sweet, a thin crust that gives, tight even crumb — the sandwich-shop roll, not a crusty baguette. Milk, butter and egg in the dough are what make it tender; bread flour is what keeps it from tearing when you split it. Fresh out of the oven they are better than anything in a bag at the store, and a day-old one comes all the way back with 5 minutes in a 350°F oven.',

  ingredients: [
    {
      id: 'milk',
      name: 'Whole milk',
      total: '2 cups',
      prep: 'warmed to about 110°F — warm to the touch, not hot',
      uses: [{ step: 1, amount: '2 cups' }],
    },
    {
      id: 'yeast',
      name: 'Instant yeast',
      total: '4½ tsp',
      prep: 'two ¼-oz packets',
      uses: [{ step: 1, amount: '4½ tsp' }],
    },
    {
      id: 'sugar',
      name: 'Sugar',
      total: '3 tbsp',
      uses: [{ step: 1, amount: '3 tbsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '2 tsp',
      uses: [{ step: 1, amount: '2 tsp' }],
    },
    {
      id: 'butter',
      name: 'Unsalted butter',
      total: '6 tbsp',
      prep: 'softened',
      uses: [{ step: 1, amount: '6 tbsp' }],
    },
    {
      id: 'eggs',
      name: 'Large eggs',
      total: '3',
      prep: '2 into the dough, 1 for the wash',
      uses: [
        { step: 1, amount: '2' },
        { step: 4, amount: '1', note: 'beaten with a splash of water for the egg wash' },
      ],
    },
    {
      id: 'bread-flour',
      name: 'Bread flour',
      total: '6 to 7 cups',
      prep: 'all-purpose works, rolls come out a little softer and tear more easily',
      uses: [
        { step: 1, amount: '6 cups' },
        { step: 1, amount: 'up to 1 cup more', note: 'a quarter cup at a time, only as needed' },
      ],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Mix and knead in the stand mixer',
      time: '12 min',
      critical: true,
      ingredientIds: ['milk', 'yeast', 'sugar', 'salt', 'butter', 'eggs', 'bread-flour'],
      body:
        'In the mixer bowl combine {2 cups|milk} of warm milk, {4½ tsp|yeast} of instant yeast, {3 tbsp|sugar} of sugar, {2 tsp|salt} of salt, {6 tbsp|butter} of softened butter, {2|eggs} eggs and {6 cups|bread-flour} of bread flour. Fit the dough hook and run on low (speed 2) until it comes together, then 6 to 8 minutes more until the dough is smooth and slaps the sides of the bowl clean. If it is still sticking to the bowl after 4 minutes, add flour a quarter cup at a time — you want tacky, not sticky: it should stick to a fingertip and pull away clean. Not sure it is kneaded enough? Do the windowpane test.',
      tip:
        'The windowpane test tells you when kneading is done. Pinch off a walnut of dough and stretch it slowly between your fingers. If it goes thin enough to see light through before it tears, the gluten is developed and you are done. If it tears right away, give it 2 more minutes.',
    },
    {
      n: 2,
      title: 'First rise',
      time: '60 min',
      ingredientIds: [],
      body:
        'Scrape the dough into a lightly oiled bowl, turn it once to coat, cover with a towel or plastic, and leave it somewhere warm until doubled, about an hour. In a cold kitchen, put it in the oven with just the light on.',
      tip:
        'Doubled means doubled — poke it with a floured finger and the dent should stay. If it springs right back, give it 15 more minutes.',
    },
    {
      n: 3,
      title: 'Divide and shape',
      time: '10 min',
      ingredientIds: [],
      body:
        'Punch the dough down and turn it onto a lightly floured counter. Divide into 8 equal pieces — a kitchen scale makes this painless, about 5 oz each. Pat each piece into a rough 6-by-4-inch rectangle, roll it up tightly from a long side into a log, pinch the seam and the ends closed, and roll it gently under your palms to even it out to about 6 inches. Set them seam-side down on two parchment-lined sheet pans, 4 to a pan, with room to grow.',
      tip:
        'Roll tight. A loosely rolled log has air pockets that become caverns in the finished roll, and a meatball falls straight through a cavern.',
    },
    {
      n: 4,
      title: 'Second rise, wash, bake',
      time: '55 min',
      critical: true,
      ingredientIds: ['eggs'],
      body:
        'Cover the pans loosely and let the rolls rise 30 to 40 minutes until puffy and about half again as big. Heat the oven to 375°F while they rise. Beat the remaining {1|eggs} egg with a splash of water and brush it over the tops — this is the egg wash — then cut one shallow slash down the length of each roll with a sharp knife. Bake 15 to 18 minutes, swapping pans halfway, until deep golden and the bottoms sound hollow when tapped. Cool on a rack at least 20 minutes before splitting.',
      tip:
        'The egg wash is what gives the shiny golden top; skip it and they come out pale and dull. The slash lets them expand up instead of splitting along the seam.',
    },
  ],

  freezing:
    'Cool completely, then bag whole rolls in a gallon freezer bag with the air pressed out. Up to 3 months. Thaw on the counter for an hour or straight into a 350°F oven from frozen for 8 to 10 minutes, which also brings the crust back.',

  reheat: [
    {
      method: 'Oven, from frozen',
      best: true,
      detail:
        '350°F for 8 to 10 minutes, right on the rack. Crust crisps back up, inside goes soft and warm. Split and load.',
    },
    {
      method: 'Counter thaw',
      detail:
        'An hour on the counter in the bag. Fine for a cold sandwich; for a hot sub, give it 5 minutes in the oven after.',
    },
    {
      method: 'Toaster oven, split',
      detail:
        'Thawed, split, cut side up, 3 to 4 minutes on toast. Quickest route to a meatball sub on a weeknight.',
    },
  ],

  glossary: [
    {
      term: 'tacky, not sticky',
      plain:
        'The right dough texture. Press a finger in — tacky dough clings for a second and lets go clean; sticky dough leaves a smear on your finger. Enriched doughs like this one are supposed to feel a little tacky. Adding flour until it stops sticking entirely gives you dry, heavy rolls.',
    },
    {
      term: 'windowpane test',
      plain:
        'Stretching a small piece of dough thin enough to see light through it without tearing. It means the gluten network is developed and the rolls will rise tall and hold their shape. Two extra minutes in the mixer usually gets a failing dough there.',
    },
    {
      term: 'egg wash',
      plain:
        'A beaten egg, thinned with a little water, brushed over dough before baking. The proteins brown and the surface goes glossy. It is the whole difference between a roll that looks homemade and one that looks like it came from a bakery.',
    },
  ],

  inspiration: [
    { label: 'The Stay At Home Chef — homemade hoagie rolls', url: 'https://thestayathomechef.com/homemade-hoagie-rolls/' },
    { label: 'King Arthur Baking — no-knead sandwich rolls', url: 'https://www.kingarthurbaking.com/blog/2016/12/07/no-knead-sandwich-rolls' },
  ],

  whereWeVeered:
    'Doubled the source to 8 rolls so one bake covers sub night and stocks the freezer, and cut the sugar a little since the meatball sauce brings its own sweetness. Switched to bread flour for a roll that holds up under sauce, added an egg wash and a slash for a proper bakery top, and wrote the whole thing around the KitchenAid so the kneading is a timer, not a workout.',
};
